import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaGraduationCap, FaUsers, FaCalendarAlt, FaStar, FaPaperPlane } from 'react-icons/fa';
import { apiService } from '../services/api';

const Mentorship = () => {
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const benefits = [
    {
      icon: FaGraduationCap,
      title: 'Expert Guidance',
      description: 'Learn from real-world experience and industry best practices'
    },
    {
      icon: FaUsers,
      title: 'Personalized Mentorship',
      description: 'One-on-one sessions tailored to your goals and skill level'
    },
    {
      icon: FaCalendarAlt,
      title: 'Flexible Schedule',
      description: 'Sessions that fit your availability and learning pace'
    },
    {
      icon: FaStar,
      title: 'Career Growth',
      description: 'Accelerate your career with targeted advice and feedback'
    }
  ];

  const experienceLevels = ['Beginner', 'Intermediate', 'Advanced', 'Professional'];
  const topics = [
    'Frontend Development',
    'Backend Development',
    'Full Stack Development',
    'Blockchain Development',
    'Embedded Systems',
    'Career Development',
    'Technical Interview Prep',
    'System Design',
    'DevOps',
    'AI/ML'
  ];

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await apiService.submitMentorship(data);
      toast.success('Mentorship request submitted successfully! I\'ll review and get back to you.');
      reset();
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Mentorship
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            Grow With <span className="gradient-text">Mentorship</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Accelerate your development career with personalized guidance and support
          </p>
        </motion.div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-2xl p-6 text-center hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                <benefit.icon className="text-2xl text-primary-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
              <p className="text-gray-400 text-sm">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Apply for Mentorship</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      {...register('full_name', { required: 'Full name is required' })}
                      type="text"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="John Doe"
                    />
                    {errors.full_name && (
                      <p className="text-red-400 text-sm mt-1">{errors.full_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email format'
                        }
                      })}
                      type="email"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Experience Level *
                  </label>
                  <select
                    {...register('experience_level', { required: 'Experience level is required' })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select your experience level</option>
                    {experienceLevels.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                  {errors.experience_level && (
                    <p className="text-red-400 text-sm mt-1">{errors.experience_level.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Goals *
                  </label>
                  <textarea
                    {...register('goals', { required: 'Goals are required' })}
                    rows="3"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="What do you hope to achieve through mentorship?"
                  />
                  {errors.goals && (
                    <p className="text-red-400 text-sm mt-1">{errors.goals.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Availability *
                  </label>
                  <input
                    {...register('availability', { required: 'Availability is required' })}
                    type="text"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Weekdays after 6pm, Weekends"
                  />
                  {errors.availability && (
                    <p className="text-red-400 text-sm mt-1">{errors.availability.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Preferred Topics *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {topics.map((topic) => (
                      <label key={topic} className="flex items-center gap-2 text-gray-300 text-sm">
                        <input
                          type="checkbox"
                          {...register('preferred_topics')}
                          value={topic}
                          className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
                        />
                        {topic}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Additional Message
                  </label>
                  <textarea
                    {...register('message')}
                    rows="3"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Any additional information you'd like to share..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : (
                    <>
                      Apply for Mentorship
                      <FaPaperPlane />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>

          <div className="lg:col-span-1">
            <div className="glass rounded-2xl p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-white mb-4">What to Expect</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <span className="text-primary-400 mt-0.5">•</span>
                  <span>Free initial consultation session</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <span className="text-primary-400 mt-0.5">•</span>
                  <span>Customized learning path based on your goals</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <span className="text-primary-400 mt-0.5">•</span>
                  <span>Weekly or bi-weekly sessions</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <span className="text-primary-400 mt-0.5">•</span>
                  <span>Code reviews and project feedback</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <span className="text-primary-400 mt-0.5">•</span>
                  <span>Career guidance and interview preparation</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <span className="text-primary-400 mt-0.5">•</span>
                  <span>Access to resources and learning materials</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mentorship;